import { useQuery } from '@tanstack/react-query'
import {
  ClipboardList, AlertTriangle, CheckCircle, Clock,
  TrendingDown, Wrench, ShieldCheck, Zap,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet } from '@/lib/api'
import { cn, formatDate, formatCurrency } from '@/lib/utils'
import { Link } from 'react-router-dom'

interface DashboardKPIs {
  open: { byPriority: Record<string, number>; total: number }
  overdueCount: number
  completedLast30Days: number
  slaBreachesLast30Days: number
  avgCostPerWoGbp: number
}

interface OpenWorkOrder {
  id: string
  woNumber: string
  title: string
  priority: string
  status: string
  slaDeadline: string | null
  asset: { name: string; code: string } | null
  room: { name: string; number: string } | null
  assignedTo: { firstName: string; lastName: string } | null
}

function KPICard({
  label, value, sub, icon: Icon, color, to,
}: {
  label: string
  value: string | number
  sub?: string
  icon: React.ElementType
  color: string
  to?: string
}) {
  const content = (
    <div className={cn('card flex items-start gap-4 hover:shadow-card-hover transition-shadow', to && 'cursor-pointer')}>
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', color)}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-2xl font-bold text-navy-800">{value}</p>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  )
  return to ? <Link to={to}>{content}</Link> : <div>{content}</div>
}

export function DashboardPage() {
  const { currentPropertyId, user } = useAuthStore()
  const pid = currentPropertyId

  const { data: kpis, isLoading: kpiLoading } = useQuery({
    queryKey: ['wo-kpis', pid],
    queryFn: () => apiGet<DashboardKPIs>(`/properties/${pid}/work-orders/dashboard/kpis`),
    enabled: !!pid,
    refetchInterval: 60_000, // refresh every minute
  })

  const { data: openWOs } = useQuery({
    queryKey: ['open-wos', pid],
    queryFn: () =>
      apiGet<{ data: OpenWorkOrder[] }>(`/properties/${pid}/work-orders?status=NEW&limit=5`),
    enabled: !!pid,
    select: (r) => r.data,
  })

  const { data: ppmData } = useQuery({
    queryKey: ['ppm-due', pid],
    queryFn: () =>
      apiGet<{ data: any[] }>(`/properties/${pid}/ppm?isActive=true&limit=5`),
    enabled: !!pid,
    select: (r) => r.data,
  })

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-0.5">{formatDate(new Date())}</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Open Work Orders"
          value={kpis?.open.total ?? '—'}
          sub={`${kpis?.open.byPriority['EMERGENCY'] ?? 0} emergency`}
          icon={ClipboardList}
          color="bg-navy-800"
          to="/work-orders"
        />
        <KPICard
          label="Overdue"
          value={kpis?.overdueCount ?? '—'}
          sub="SLA breached"
          icon={AlertTriangle}
          color={kpis && kpis.overdueCount > 0 ? 'bg-red-600' : 'bg-gray-400'}
          to="/work-orders?overdueOnly=true"
        />
        <KPICard
          label="Completed (30 days)"
          value={kpis?.completedLast30Days ?? '—'}
          sub={`${kpis?.slaBreachesLast30Days ?? 0} SLA breaches`}
          icon={CheckCircle}
          color="bg-emerald-600"
        />
        <KPICard
          label="Avg Cost / WO"
          value={formatCurrency(kpis?.avgCostPerWoGbp)}
          sub="last 30 days"
          icon={TrendingDown}
          color="bg-gold-500"
        />
      </div>

      {/* Priority breakdown */}
      {kpis && (
        <div className="card">
          <h2 className="text-sm font-semibold text-navy-800 mb-4">Open Work Orders by Priority</h2>
          <div className="flex gap-3 flex-wrap">
            {['EMERGENCY', 'URGENT', 'HIGH', 'STANDARD', 'LOW', 'PLANNED'].map((p) => (
              <div key={p} className="flex items-center gap-2">
                <span className={`badge badge-${p.toLowerCase()}`}>{p}</span>
                <span className="text-sm font-semibold text-navy-800">{kpis.open.byPriority[p] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Open Work Orders */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-gold-500" />
              New Work Orders
            </h2>
            <Link to="/work-orders" className="text-xs text-navy-500 hover:text-navy-800">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {openWOs?.length === 0 && (
              <p className="text-sm text-gray-400 py-4 text-center">No open work orders 🎉</p>
            )}
            {openWOs?.map((wo) => (
              <Link
                key={wo.id}
                to={`/work-orders/${wo.id}`}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`badge badge-${wo.priority.toLowerCase()}`}>
                      {wo.priority}
                    </span>
                    <span className="text-xs text-gray-400">{wo.woNumber}</span>
                  </div>
                  <p className="text-sm font-medium text-navy-800 truncate">{wo.title}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {wo.asset?.name ?? ''} {wo.room ? `• Room ${wo.room.number}` : ''}
                  </p>
                </div>
                {wo.slaDeadline && (
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-gray-400">Due</p>
                    <p className={cn('text-xs font-medium',
                      new Date(wo.slaDeadline) < new Date() ? 'text-red-600' : 'text-gray-600')}>
                      {formatDate(wo.slaDeadline, 'HH:mm dd MMM')}
                    </p>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* PPM Due */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-gold-500" />
              PPM Due Soon
            </h2>
            <Link to="/ppm" className="text-xs text-navy-500 hover:text-navy-800">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {ppmData?.length === 0 && (
              <p className="text-sm text-gray-400 py-4 text-center">No PPM tasks due soon</p>
            )}
            {ppmData?.map((ppm: any) => (
              <div key={ppm.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {ppm.isCompliance && (
                      <span className="badge bg-purple-100 text-purple-800">Compliance</span>
                    )}
                    {ppm.isOverdue && (
                      <span className="badge bg-red-100 text-red-800">Overdue</span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-navy-800 truncate">{ppm.title}</p>
                  <p className="text-xs text-gray-400 truncate">{ppm.asset?.name}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-400">Due</p>
                  <p className={cn('text-xs font-medium',
                    ppm.isOverdue ? 'text-red-600' : ppm.daysUntilDue <= 7 ? 'text-amber-600' : 'text-gray-600')}>
                    {formatDate(ppm.nextDueDate)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Alerts */}
      <ComplianceAlerts />
    </div>
  )
}

function ComplianceAlerts() {
  const { data } = useQuery({
    queryKey: ['compliance-alerts'],
    queryFn: () => apiGet<{ alerts: { type: string; severity: string; title: string; detail: string }[]; total: number }>('/docflow/compliance-alerts'),
  })

  if (!data?.alerts?.length) return null

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-red-500" />
          Compliance Alerts
        </h2>
        <Link to="/compliance" className="text-xs text-navy-500 hover:text-navy-800">
          View all →
        </Link>
      </div>
      <div className="space-y-2">
        {data.alerts.slice(0, 5).map((alert, i) => (
          <div key={i} className={cn('flex items-start gap-2 p-2 rounded-lg text-xs', {
            'bg-red-50 text-red-800': alert.severity === 'critical',
            'bg-amber-50 text-amber-800': alert.severity === 'warning',
            'bg-blue-50 text-blue-800': alert.severity === 'info',
          })}>
            <AlertTriangle className={cn('w-3.5 h-3.5 flex-shrink-0 mt-0.5', {
              'text-red-500': alert.severity === 'critical',
              'text-amber-500': alert.severity === 'warning',
              'text-blue-500': alert.severity === 'info',
            })} />
            <div>
              <p className="font-medium">{alert.title}</p>
              <p className="opacity-75 mt-0.5">{alert.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
